import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useActorEmails, fmtAuthor } from "@/hooks/use-actor-emails";


export const Route = createFileRoute("/_authenticated/admin/linkedin")({
  
  head: () => ({ meta: [{ title: "Admin — Linkedin | Mine Class" }, { name: "description", content: "Panel de administración de Linkedin — acceso restringido a Mine Class." }, { name: "robots", content: "noindex, nofollow" }] }),
  component: LinkedInAdmin,
});

const BUCKET = "media";
const SLOTS = [1, 2, 3, 4];
const LINKEDIN_URL = "https://www.linkedin.com/school/mine-class/";

type Post = {
  id: string;
  image_url: string | null;
  post_url: string | null;
  caption: string | null;
  embed_html: string | null;
  sort_order: number;
  is_active: boolean;
  created_at: string;
  created_by: string | null;
  updated_by: string | null;
};

type SlotDraft = {
  id: string | null;
  embed_html: string;
  caption: string;
  post_url: string;
  image_url: string;
  created_by: string | null;
  updated_by: string | null;
};

const emptyDraft = (): SlotDraft => ({
  id: null, embed_html: "", caption: "", post_url: "", image_url: "",
  created_by: null, updated_by: null,
});


function extractEmbedSrc(html: string): string | null {
  const m = html.match(/<iframe[^>]*\ssrc=["']([^"']+)["']/i);
  return m ? m[1] : null;
}

function LinkedInAdmin() {
  const [drafts, setDrafts] = useState<Record<number, SlotDraft>>({
    1: emptyDraft(), 2: emptyDraft(), 3: emptyDraft(), 4: emptyDraft(),
  });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const lookup = useActorEmails(
    SLOTS.flatMap((s) => [drafts[s].created_by, drafts[s].updated_by]),
  );


  async function load() {
    const { data, error } = await (supabase as any)
      .from("linkedin_posts")
      .select("*")
      .order("sort_order", { ascending: true })
      .order("created_at", { ascending: false });
    if (error) { setErr(error.message); return; }
    const rows = (data ?? []) as Post[];
    const next: Record<number, SlotDraft> = { 1: emptyDraft(), 2: emptyDraft(), 3: emptyDraft(), 4: emptyDraft() };
    // Prefer rows with sort_order 1-4; fall back to filling remaining slots
    const used = new Set<string>();
    for (const slot of SLOTS) {
      const row = rows.find((r) => r.sort_order === slot && !used.has(r.id));
      if (row) {
        used.add(row.id);
        next[slot] = {
          id: row.id,
          embed_html: row.embed_html ?? "",
          caption: row.caption ?? "",
          post_url: row.post_url ?? "",
          image_url: row.image_url ?? "",
          created_by: row.created_by,
          updated_by: row.updated_by,
        };

      }
    }
    const leftovers = rows.filter((r) => !used.has(r.id));
    for (const slot of SLOTS) {
      if (next[slot].id) continue;
      const row = leftovers.shift();
      if (!row) break;
      next[slot] = {
        id: row.id,
        embed_html: row.embed_html ?? "",
        caption: row.caption ?? "",
        post_url: row.post_url ?? "",
        image_url: row.image_url ?? "",
        created_by: row.created_by,
        updated_by: row.updated_by,
      };

    }
    setDrafts(next);
  }

  useEffect(() => { load(); }, []);

  function update(slot: number, patch: Partial<SlotDraft>) {
    setDrafts((d) => ({ ...d, [slot]: { ...d[slot], ...patch } }));
  }

  function clearSlot(slot: number) {
    setDrafts((d) => ({ ...d, [slot]: { ...emptyDraft(), id: d[slot].id } }));
  }

  async function onUploadImage(slot: number, e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    setMsg(null);
    try {
      const userId = (await supabase.auth.getUser()).data.user?.id;
      const path = `${userId}/linkedin/${Date.now()}-${file.name.replace(/[^a-zA-Z0-9._-]/g, "_")}`;
      const { error: upErr } = await supabase.storage.from(BUCKET).upload(path, file, {
        contentType: file.type, upsert: false,
      });
      if (upErr) throw upErr;
      const { data: pub } = supabase.storage.from(BUCKET).getPublicUrl(path);
      update(slot, { image_url: pub.publicUrl });
      setMsg("Imagen cargada — recuerda 'Guardar todo'");
    } catch (e) {
      setMsg(e instanceof Error ? e.message : "Error");
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  }

  async function saveAll() {
    setBusy(true);
    setErr(null);
    setMsg(null);
    try {
      const userId = (await supabase.auth.getUser()).data.user?.id;
      for (const slot of SLOTS) {
        const d = drafts[slot];
        const hasContent = d.embed_html.trim() || d.image_url.trim();
        if (!hasContent) {
          if (d.id) {
            const { error } = await (supabase as any).from("linkedin_posts").delete().eq("id", d.id);
            if (error) throw error;
          }
          continue;
        }
        const payload = {
          embed_html: d.embed_html.trim() || null,
          caption: d.caption.trim() || null,
          post_url: d.post_url.trim() || null,
          image_url: d.image_url.trim() || null,
          sort_order: slot,
          is_active: true,
        };
        if (d.id) {
          const { error } = await (supabase as any).from("linkedin_posts").update(payload).eq("id", d.id);
          if (error) throw error;
        } else {
          const { error } = await (supabase as any).from("linkedin_posts").insert({ ...payload, created_by: userId });
          if (error) throw error;
        }
      }
      setMsg("Todos los espacios actualizados");
      await load();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Error guardando");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-6">
      <div className="bg-white border border-[var(--color-outline-variant)] p-4 rounded-lg">
        <h2 className="font-bold mb-1">Publicaciones LinkedIn (4 espacios fijos)</h2>
        <p className="text-sm text-[var(--color-on-surface-variant)]">
          Pega el código de <strong>"Insertar en un sitio web"</strong> desde LinkedIn — abre el post,
          clic en los 3 puntos → <em>Insertar</em>, copia el <code>&lt;iframe&gt;</code> y pégalo en el espacio deseado.
          Cambia lo que quieras en los 4 espacios y presiona <strong>Guardar todo</strong>.
          Todos los botones de la web llevan a{" "}
          <a href={LINKEDIN_URL} target="_blank" rel="noreferrer" className="underline text-[#0077b5]">
            linkedin.com/school/mine-class
          </a>.
        </p>
      </div>

      <div className="sticky top-0 z-10 bg-[var(--color-surface-container-low)] py-2 flex items-center gap-3">
        <button
          onClick={saveAll}
          disabled={busy}
          className="px-5 py-2.5 bg-[#0077b5] text-white text-sm font-bold rounded-md hover:brightness-110 disabled:opacity-50"
        >
          {busy ? "Guardando..." : "Guardar todo"}
        </button>
        <button
          onClick={load}
          disabled={busy}
          className="px-4 py-2 border border-[var(--color-outline-variant)] text-sm rounded-md hover:bg-white disabled:opacity-50"
        >
          Descartar cambios
        </button>
        {msg && <span className="text-xs text-green-700">{msg}</span>}
        {err && <span className="text-xs text-red-600">{err}</span>}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {SLOTS.map((slot) => {
          const d = drafts[slot];
          const embedSrc = d.embed_html ? extractEmbedSrc(d.embed_html) : null;
          return (
            <div key={slot} className="bg-white border border-[var(--color-outline-variant)] rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="font-bold text-sm">Espacio {slot}</h3>
                <button onClick={() => clearSlot(slot)} className="text-xs text-red-600 hover:underline">
                  Vaciar
                </button>
              </div>
              {d.id && (
                <div className="text-[11px] text-[var(--color-on-surface-variant)] -mt-1">
                  {fmtAuthor(lookup, d.created_by, d.updated_by)}
                </div>
              )}


              <label className="block text-xs font-semibold uppercase tracking-wide text-[var(--color-on-surface-variant)]">
                Código insertar (iframe de LinkedIn)
              </label>
              <textarea
                value={d.embed_html}
                onChange={(e) => update(slot, { embed_html: e.target.value })}
                placeholder='<iframe src="https://www.linkedin.com/embed/feed/update/urn:li:share:..." ...></iframe>'
                rows={4}
                className="w-full border border-[var(--color-outline-variant)] px-3 py-2 text-xs rounded-md font-mono"
              />

              <details className="text-xs">
                <summary className="cursor-pointer text-[var(--color-on-surface-variant)]">
                  Alternativa: imagen + enlace
                </summary>
                <div className="mt-2 space-y-2">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => onUploadImage(slot, e)}
                    disabled={busy}
                    className="text-xs"
                  />
                  <input
                    placeholder="URL de imagen"
                    value={d.image_url}
                    onChange={(e) => update(slot, { image_url: e.target.value })}
                    className="w-full border border-[var(--color-outline-variant)] px-3 py-2 text-xs rounded-md"
                  />
                  <input
                    placeholder="URL del post en LinkedIn (opcional)"
                    value={d.post_url}
                    onChange={(e) => update(slot, { post_url: e.target.value })}
                    className="w-full border border-[var(--color-outline-variant)] px-3 py-2 text-xs rounded-md"
                  />
                </div>
              </details>

              <input
                placeholder="Descripción corta (opcional)"
                value={d.caption}
                onChange={(e) => update(slot, { caption: e.target.value })}
                className="w-full border border-[var(--color-outline-variant)] px-3 py-2 text-xs rounded-md"
              />

              {(embedSrc || d.image_url) && (
                <div className="border-t border-[var(--color-outline-variant)] pt-3">
                  <div className="text-xs text-[var(--color-on-surface-variant)] mb-2">Vista previa</div>
                  {embedSrc ? (
                    <iframe src={embedSrc} title={`Preview ${slot}`} className="w-full h-72 border-0" allowFullScreen />
                  ) : (
                    <img src={d.image_url} alt="" className="w-full h-40 object-cover rounded" />
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
